import { type PuckContext } from "@measured/puck";
import { contentColumnsGridAttrs } from "@/lib/cms-puck/blocks/column-count";
import { type MediaSize } from "@/components/cms-puck/fields/MediaPickerField";
import { normalizeAttachmentIds } from "@/lib/cms-puck/blocks/gallery";
import { type ButtonLink } from "@/lib/cms-puck/blocks/button";
import { LayoutSlot } from "@/components/cms-puck/layout/LayoutSlot";
import {
  LOGO_SIZE_CLASS,
  normalizeLogoGridColumns,
  normalizeLogoGridSize,
} from "@/lib/cms-puck/blocks/logo-grid";
import {
  activeTestimonialItems,
  normalizeTestimonialItem,
  type TestimonialItem,
  type TestimonialsLayout,
} from "@/lib/cms-puck/blocks/testimonials";
import {
  normalizeVideoHeroHeight,
  VIDEO_HERO_OVERLAY_CLASS,
  type VideoHeroOverlay,
} from "@/lib/cms-puck/blocks/video-hero";
import { type BlockStyle } from "@/lib/cms-puck/block-style";
import { CMS_FLOW_CLASS } from "@/lib/cms-puck/block-style/defaults";
import { useBlockTextCss } from "@/hooks/use-block-style-css";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import { type MediaAspect } from "@/components/cms-puck/blocks/shared";
import { MediaPreview } from "@/components/cms-puck/blocks/MediaPreview";
import {
  resolveCompositeInnerLayout,
  type LayoutAlign,
  type LayoutGap,
} from "@/lib/cms-puck/layout";

export const MEDIA_ASPECT_CLASS: Record<Exclude<MediaAspect, "auto">, string> = {
  "1/1": "aspect-square",
  "4/3": "aspect-[4/3]",
  "3/2": "aspect-[3/2]",
  "16/9": "aspect-video",
};

export function LogoGridBlockPreview({
  attachmentIds,
  columns,
  size,
  grayscale,
}: {
  attachmentIds: string[];
  columns: ReturnType<typeof normalizeLogoGridColumns>;
  size: ReturnType<typeof normalizeLogoGridSize>;
  grayscale: boolean;
}) {
  const ids = normalizeAttachmentIds(attachmentIds);
  if (ids.length === 0) {
    return (
      <div className="text-sm text-slate-400">
        Ajoutez des logos partenaires
      </div>
    );
  }
  const cols = normalizeLogoGridColumns(columns);
  const grid = contentColumnsGridAttrs(cols);
  return (
    <div
      className={`${grid.className} gap-6 items-center`}
      style={grid.style}
    >
      {ids.map((id) => (
        <div key={id} className="flex items-center justify-center">
          <MediaPreview
            attachmentId={id}
            size="medium"
            className={`w-auto object-contain ${LOGO_SIZE_CLASS[size]} ${grayscale ? "grayscale opacity-70" : ""}`}
          />
        </div>
      ))}
    </div>
  );
}

export function TestimonialsBlockPreview({
  item1,
  item2,
  item3,
  layout,
  gap,
  gapCustomPx,
  style,
}: {
  item1: TestimonialItem;
  item2: TestimonialItem;
  item3: TestimonialItem;
  layout: TestimonialsLayout;
  gap?: LayoutGap | null;
  gapCustomPx?: number | null;
  style?: BlockStyle;
}) {
  const items = activeTestimonialItems([
    normalizeTestimonialItem(item1),
    normalizeTestimonialItem(item2),
    normalizeTestimonialItem(item3),
  ]);
  const textCss = useBlockTextCss(style);
  if (items.length === 0) {
    return (
      <div className="text-sm text-slate-400">
        {cmsNew(
          "cms.new.preview.testimonials.empty",
          "Ajoutez des témoignages",
        )}
      </div>
    );
  }
  const inner = resolveCompositeInnerLayout({
    gap,
    gapCustomPx,
    withAlignItems: false,
  });
  const gridClass =
    layout === "carousel"
      ? `flex ${inner.className} overflow-x-auto pb-2`
      : `grid ${inner.className} md:grid-cols-2 lg:grid-cols-3`;
  return (
    <div className={gridClass} style={inner.style}>
      {items.map((item, i) => (
        <figure
          key={i}
          className="min-w-[240px] rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
        >
          <blockquote className="text-sm italic text-slate-700" style={textCss}>
            « {item.quote} »
          </blockquote>
          <figcaption className="mt-4 flex items-center gap-3">
            {item.imageAttachmentId ? (
              <MediaPreview
                attachmentId={item.imageAttachmentId}
                size="petit"
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : null}
            <div>
              <div
                className="text-sm font-semibold text-slate-900"
                style={textCss}
              >
                {item.author}
              </div>
              {item.role ? (
                <div className="text-xs text-slate-500" style={textCss}>
                  {item.role}
                </div>
              ) : null}
            </div>
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

export function VideoHeroBlockPreview({
  videoAttachmentId,
  posterAttachmentId,
  title,
  subtitle,
  ctaLabel,
  link: _link,
  overlay,
  height,
  gap,
  gapCustomPx,
  alignItems,
  style,
}: {
  videoAttachmentId: string;
  posterAttachmentId: string;
  title: string;
  subtitle: string;
  ctaLabel: string;
  link: ButtonLink;
  overlay: VideoHeroOverlay;
  height: number;
  gap?: LayoutGap | null;
  gapCustomPx?: number | null;
  alignItems?: LayoutAlign | null;
  style?: BlockStyle;
}) {
  const textCss = useBlockTextCss(style);
  const h = normalizeVideoHeroHeight(height);
  const posterId = posterAttachmentId || videoAttachmentId;
  const inner = resolveCompositeInnerLayout({
    gap,
    gapCustomPx,
    alignItems,
    alignFallback: "center",
  });
  return (
    <section
      className="relative flex w-full items-center justify-center overflow-hidden"
      style={{ minHeight: h }}
    >
      <div className="absolute inset-0">
        <MediaPreview
          attachmentId={posterId}
          size="large"
          className="h-full w-full object-cover"
        />
      </div>
      {overlay !== "none" ? (
        <div
          className={`absolute inset-0 ${VIDEO_HERO_OVERLAY_CLASS[overlay]}`}
        />
      ) : null}
      <div
        className={`relative z-10 mx-auto flex max-w-3xl flex-col px-6 py-16 text-center text-white ${inner.className}`}
        style={inner.style}
      >
        {title ? (
          <h1 className="text-4xl font-semibold tracking-tight" style={textCss}>
            {title}
          </h1>
        ) : null}
        {subtitle ? (
          <p className="mx-auto max-w-xl text-lg text-white/90" style={textCss}>
            {subtitle}
          </p>
        ) : null}
        {ctaLabel ? (
          <div>
            <span className="inline-flex rounded-md bg-white px-5 py-2.5 text-sm font-semibold text-slate-900">
              {ctaLabel}
            </span>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/**
 * MediaBlock — média (image/vidéo/embed) + légende. Traitement du texte (onglet
 * Design) appliqué INLINE sur la légende. Parité front : PuckMedia.
 */
/** Cadre max convertisseur (`fit:inside`) — parité labels « Taille (image) ». */
const MEDIA_SIZE_MAX_W_CLASS: Record<MediaSize, string> = {
  petit: "max-w-[100px]",
  medium: "max-w-[600px]",
  large: "max-w-[1600px]",
  original: "max-w-full",
};

export function MediaBlock({
  attachmentId,
  caption,
  size,
  aspect,
  fullWidth = false,
  style,
}: {
  attachmentId?: string;
  caption?: string;
  size?: MediaSize;
  aspect?: MediaAspect;
  /**
   * Ajuste l'image à la largeur du bloc : lève le plafond `size` + le `max-w-4xl`
   * de la figure. `object-fit` reste piloté par `aspect`. Parité front PuckMedia.
   * (Le réglage « cliquable » n'a pas d'effet ici : l'aperçu BO n'ouvre pas le
   * plein écran.)
   */
  fullWidth?: boolean;
  style?: BlockStyle;
}) {
  const textCss = useBlockTextCss(style);
  const resolvedSize: MediaSize = size ?? "large";
  const sizeMaxW = fullWidth ? "" : MEDIA_SIZE_MAX_W_CLASS[resolvedSize];
  // Pas de gouttière hardcodée (`px-*`) : spacing via onglet Design. Pleine
  // largeur = lève le plafond `max-w-4xl` (parité front PuckMedia).
  const figureWidthClass = fullWidth ? "w-full max-w-none" : "max-w-4xl";
  return (
    <figure className={`mx-auto ${figureWidthClass}`}>
      {aspect && aspect !== "auto" ? (
        // Ratio FIXE : conteneur borné à la taille choisie + object-cover.
        <div
          className={`mx-auto w-full overflow-hidden rounded ${sizeMaxW} ${MEDIA_ASPECT_CLASS[aspect]}`}
        >
          <MediaPreview
            attachmentId={attachmentId}
            size={resolvedSize}
            className="h-full w-full object-cover"
          />
        </div>
      ) : (
        // Auto : taille INTRINSÈQUE du fichier (ou pleine largeur si demandé),
        // pas de plafond 480px qui égalisait les tailles.
        <MediaPreview
          attachmentId={attachmentId}
          size={resolvedSize}
          className={`mx-auto h-auto ${fullWidth ? "w-full" : "w-auto max-w-full"} object-contain ${sizeMaxW}`}
        />
      )}
      {caption && (
        <figcaption
          className="mt-2 text-center text-sm text-slate-500"
          style={textCss}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/** Alignement propre de la colonne texte de Texte + image, selon la section. */
const TEXT_COLUMN_SELF: Record<LayoutAlign, string> = {
  start: "self-start",
  center: "self-center",
  end: "self-end",
  stretch: "self-center",
};

/**
 * TextImageBlock — section éditoriale : colonne de blocs imbriqués + image.
 * Le slot `content` accepte Titre, Texte, Bouton, etc. (ordre libre).
 * Parité front : PuckTextImage.
 */
export function TextImageBlock({
  id,
  puck,
  imageAttachmentId,
  sampleUrl,
  imagePosition,
  imageFrame = "card",
  container = "contained",
  paddingY = "none",
  imageOnMobile = "show",
  gap,
  gapCustomPx,
  alignItems,
  imageCaption,
  content: Content,
}: {
  id: string;
  puck: PuckContext;
  imageAttachmentId?: string;
  /** Image d'exemple (aperçu bibliothèque uniquement) — cf. MediaPreview.sampleUrl. */
  sampleUrl?: string;
  imagePosition: "left" | "right";
  /** `card` (défaut, cadre arrondi+fond) · `none` (image nue). KEEP IN SYNC avec front PuckTextImage. */
  imageFrame?: "card" | "none";
  /** `contained` (défaut, max-w-6xl) · `full` (max-w-screen-2xl). */
  container?: "contained" | "full";
  /** `none` (défaut, py-0) · `sm` (py-8) · `lg` (py-16). */
  paddingY?: "lg" | "sm" | "none";
  /** Image en mobile (< md) : `show` (défaut) · `hide`. KEEP IN SYNC avec front. */
  imageOnMobile?: "show" | "hide";
  /** Espacement image ↔ texte (absent = none). */
  gap?: LayoutGap | null;
  gapCustomPx?: number | null;
  /** Alignement transversal (défaut center = rendu historique). */
  alignItems?: LayoutAlign | null;
  /** Légende / crédits sous l'image (traduisible). */
  imageCaption?: string;
  content: React.ComponentType<{
    minEmptyHeight?: number;
    className?: string;
    style?: React.CSSProperties;
  }>;
}) {
  const imageFirst = imagePosition === "left";
  const naked = imageFrame === "none";
  const hasImage = !!sampleUrl || !!imageAttachmentId;
  let imageBlock: React.ReactNode;
  if (!hasImage) {
    // Aucune image : TOUJOURS un placeholder visible (canvas éditeur + aperçu
    // modèle) — même en mode « nu », pour que la vignette reste représentative.
    imageBlock = (
      <div className="flex min-h-64 items-center justify-center rounded-lg bg-slate-100 text-xs text-slate-400">
        {cmsNew("cms.new.preview.textImage.mediaPlaceholder", "Média")}
      </div>
    );
  } else if (naked) {
    imageBlock = (
      <MediaPreview
        attachmentId={imageAttachmentId}
        sampleUrl={sampleUrl}
        className="h-auto w-full object-contain"
      />
    );
  } else {
    imageBlock = (
      <div className="min-h-64 overflow-hidden rounded-lg bg-slate-100">
        <MediaPreview
          attachmentId={imageAttachmentId}
          sampleUrl={sampleUrl}
          className="h-full w-full object-cover"
        />
      </div>
    );
  }
  const caption = imageCaption?.trim();
  const ImageContent = caption ? (
    <div className="flex flex-col gap-2">
      {imageBlock}
      {/* Parité front : héros fidèle (`imageFrame="none"`) = légende `mb-2 ml-3`,
          centrée en mobile puis à gauche. */}
      <p
        className={
          naked
            ? "mb-2 ml-3 text-center text-sm text-slate-500 md:text-left"
            : "text-sm text-slate-500"
        }
      >
        {caption}
      </p>
    </div>
  ) : (
    imageBlock
  );
  // Masquage mobile : enveloppe seulement si demandé (markup inchangé sinon).
  const ImageCol =
    imageOnMobile === "hide" ? (
      <div className="hidden md:block">{ImageContent}</div>
    ) : (
      ImageContent
    );
  // La colonne texte suit l'alignement vertical de la section, comme le rendu
  // public. Jamais étirée : le `self-stretch` interne du LayoutSlot collerait le
  // contenu en haut, alors qu'une colonne étirée au front centre son contenu
  // (`justify-center`) — « Étiré » se montre donc centré, comme au front.
  const TextCol = (
    <div
      className={`flex flex-col justify-center ${TEXT_COLUMN_SELF[alignItems ?? "center"]}`}
    >
      <LayoutSlot
        slot={Content}
        slotKey="content"
        componentId={id}
        variant="container"
        layoutDirection="column"
        puck={puck}
      />
    </div>
  );
  const maxWidthClass = container === "full" ? "max-w-screen-2xl" : "max-w-6xl";
  const paddingYClass =
    paddingY === "none" ? "py-0" : paddingY === "sm" ? "py-8" : "py-16";
  const inner = resolveCompositeInnerLayout({
    gap,
    gapCustomPx,
    alignItems,
    alignFallback: "center",
  });
  return (
    <section
      // `CMS_FLOW_CLASS` : cible des surcharges Disposition tablette / mobile.
      className={`${CMS_FLOW_CLASS} mx-auto grid ${maxWidthClass} grid-cols-1 ${inner.className} ${paddingYClass} md:grid-cols-2`}
      style={inner.style}
    >
      {imageFirst ? (
        <>
          {ImageCol}
          {TextCol}
        </>
      ) : (
        <>
          {TextCol}
          {ImageCol}
        </>
      )}
    </section>
  );
}
